import { useEffect, useState } from 'react';
import { parseCoverDoc } from 'fym-shared/src/music24/coverDoc';
import { useGetCoverMakerLeadQuery } from '../../../redux/api/fymAPI';
import type { CoverBankBackground } from '../../../redux/api/fymAPI.types';
import { getFullAPIFileURL } from '../../common/getFullAPIFileURL';
import { isLocalUpload, publicFirstDoc, withLocalUpload } from './publicCoverData';
import { browserCoverStorage, readPublicCover, type PublicCover } from './publicCoverStore';
import { loadPublicUpload } from './publicUploadStore';

/** What the tool says when it opens: a design link that does not work, or an own picture that is gone. */
export type PublicCoverNote = 'loadFailed' | 'uploadLost' | null;

export interface PublicCoverStart {
  cover: PublicCover;
  note: PublicCoverNote;
}

interface UsePublicCoverSourceProps {
  /** The bank; undefined while it loads. */
  backgrounds: CoverBankBackground[] | undefined;
  /** The lead token of `?design=<token>`, or null. */
  token: string | null;
  /** The text of the title and artist boxes of a new design, while the two fields are empty. */
  placeholders: { title: string; artist: string };
}

/**
 * The design that the public tool opens with (spec §2, design g1): the saved design of a lead link
 * (`?design=<token>`), else the design of this browser (`m24CoverMakerDoc`), else a new one: the
 * default layout on a bank picture, so the tool never opens empty. An own picture of an earlier
 * visit comes back from IndexedDB. Null while the bank or the lead loads. It resolves once.
 */
export function usePublicCoverSource({
  backgrounds,
  token,
  placeholders,
}: UsePublicCoverSourceProps): PublicCoverStart | null {
  const lead = useGetCoverMakerLeadQuery({ token: token ?? '' }, { skip: token === null });
  const [start, setStart] = useState<PublicCoverStart | null>(null);
  const isLeadSettled = token === null || lead.data !== undefined || lead.error !== undefined;

  useEffect(() => {
    if (start !== null || backgrounds === undefined || !isLeadSettled) {
      return undefined;
    }
    let isCancelled = false;
    const resolve = async (): Promise<PublicCoverStart> => {
      const leadDoc = lead.data === undefined ? null : parseCoverDoc(lead.data.doc);
      if (leadDoc !== null) {
        return { cover: { doc: leadDoc, title: lead.data?.title ?? '', artist: lead.data?.artist ?? '' }, note: null };
      }
      const failedNote: PublicCoverNote = token === null ? null : 'loadFailed';
      const saved = readPublicCover(browserCoverStorage());
      if (saved !== null) {
        const blob = isLocalUpload(saved.doc.background) ? await loadPublicUpload() : null;
        const restored = withLocalUpload(saved.doc, blob === null ? null : URL.createObjectURL(blob));
        return { cover: { ...saved, doc: restored.doc }, note: restored.lost ? 'uploadLost' : failedNote };
      }
      const doc = publicFirstDoc({ ...placeholders, backgrounds, toUrl: getFullAPIFileURL });
      return { cover: { doc, title: '', artist: '' }, note: failedNote };
    };
    resolve()
      .then((resolved) => {
        if (!isCancelled) {
          setStart(resolved);
        }
      })
      .catch(console.error);
    return () => {
      isCancelled = true;
    };
  }, [backgrounds, isLeadSettled, lead.data, placeholders, start, token]);

  return start;
}
